import React from "react";
import styled from "styled-components";

import parse from "../util/html-react-parser";

import theme from "../../styles/theme";
import { style as h1 } from "./headings/H1";
import { style as h2 } from "./headings/H2";
import { style as h3 } from "./headings/H3";

import BlueQuote from "../../images/icons/quotemarks/quote_blue.svg";
import GreenQuote from "../../images/icons/quotemarks/quote_green.svg";
import OrangeQuote from "../../images/icons/quotemarks/quote_orange.svg";
import YellowQuote from "../../images/icons/quotemarks/quote_yellow.svg";

const quotes = [BlueQuote, GreenQuote, OrangeQuote, YellowQuote];
const {
	font,
	colors,
	breakPoints: { screenQuery, mobileFirstScreenQuery },
} = theme;

const HTMLWrapper = styled.div`
	/* default font for divs that are just raw doggin text in em: */
	font-size: 16px;
	line-height: 24px;
	${mobileFirstScreenQuery.medium} {
		font-size: 18px;
		line-height: 27px;
	}

	font-family: "ProximaNova-Regular", "Helvetica Neue", Helvetica, Arial, sans-serif;

	strong {
		font-family: "ProximaNova-Bold";
	}
	b {
		font-family: "ProximaNova-SemiBold";
	}

	h1,
	h2 {
		font-family: "RecoletaAlt-Semibold";
	}
	h3,
	h4,
	h5,
	h6 {
		font-family: "ProximaNova-Semibold";
		font-weight: 600;
	}
	h1 {
		${h1}
	}
	h2 {
		${h2}
	}
	h3 {
		${h3}
	}
	h4 {
		font-size: 24px;
		line-height: 28px;
		${screenQuery.extraLarge} {
			font-size: 22px;
			line-height: 26px;
		}
		${screenQuery.medium} {
			font-size: 20px;
			line-height: 24px;
		}
	}
	h5 {
		font-size: 22px;
		line-height: 26px;
		${screenQuery.extraLarge} {
			font-size: 20px;
			line-height: 24px;
		}
		${screenQuery.medium} {
			font-size: 18px;
			line-height: 22px;
		}
	}
	h6 {
		font-size: 20px;
		line-height: 22px;
		${screenQuery.extraLarge} {
			font-size: 18px;
			line-height: 20px;
		}
		${screenQuery.medium} {
			font-size: 16px;
			line-height: 18px;
		}
	}
	p:not([class^="Body"]):not([class^="AllCaps"]) ~ *:not(p):not(ul):not(ol):not(cite) {
		/* margin-top: 60px; */
		${theme.gridMarginTop}
	}

	/* if p's are stacked, put a margin-top on following ones: */
	p + p {
		margin-top: 15px;
	}
	/* for some reason there's always a stray <p> at the end of it: */
	p:empty {
		display: none;
	}

	a {
		font-family: ${font.bold};
		font-size: 16px;
		color: ${(props) => props.color || colors.newBrand.turquoise};
		text-decoration: underline;
	
		:hover {
			color: ${colors.newBrand.darkTurquoise};
			text-decoration: underline;
		}
	}

	${(props) =>
		props.dropCap
			? `> p:first-of-type:first-letter {
        float: left;
        font-size: 45px;
        line-height: 35px;
        padding-top: 6px;
        padding-right: 4px;
        padding-left: 0px;
        @media only screen and (max-width: 1279px) {
            font-size: 35px;
            line-height: 28px;
            padding-top: 6px;
            padding-right: 2px;
        }
    }`
			: null}

	color: ${(props) => (props.color ? colors[props.color] : "")};
	*:not(a),
	*:before,
	*:after {
		color: ${(props) => (props.color ? colors[props.color] : "")};
	}

	/* ---------------- blockquote ------------------------ */
	blockquote {
		font-size: 1.4em;
		padding: 0px 0 0px 80px;
		line-height: 1.6;
		position: relative;
		/* p {
            font-family: "ProximaNova-Regular"; 
            font-size: 20px; 
            line-height: 30px;
            margin-bottom: 5px;
        } */
		p:first-of-type {
			margin-bottom: 30px;
			padding-top: 15px;
		}
		b {
			margin-bottom: 200px;
			font-family: "ProximaNova-Bold";
		}
		cite {
			display: block;
			font-family: "ProximaNova-Semibold";
			font-style: normal;
		}
	}

	blockquote::before {
		position: absolute;
		left: 0px;
		background-image: url(${(props) =>
			props.quoteMark || quotes[Math.floor(Math.random() * quotes.length)]});
		background-size: 60px 45px;
		display: inline-block;
		width: 60px;
		height: 45px;
		content: "";
	}
	@media only screen and (max-width: 576px) {
		video {
			width: 100vw;
			position: relative;
			margin-left: -50vw;
			left: 50%;
		}
	}
	/* ---------------- lists ------------------------ */

	/* just in case there's p's wrapped in li's: */
	li p {
		margin-bottom: 0px;
	}
	ol {
		list-style: none;
		counter-reset: item;
		li {
			counter-increment: item;
			width: 100%;
			padding-left: 1em;
			margin-bottom: 15px;
			position: relative;
			p {
				display: inline-block;
				padding-left: 10px;
			}
		}
		li:before {
			margin-right: 10px;
			content: counter(item) ".";
			position: absolute;
			vertical-align: top;
			font-size: 1em;
			color: ${(props) => (props.discColor ? props.discColor : colors.orange)};
			left: 0;
		}
	}
	ul {
		list-style: none;
		margin: 15px 0;

		${screenQuery.small} {
			grid-template-columns: 1fr;
		}
		li {
			width: 100%;
			padding-left: ${(props) => (props.noDisc ? 0 : "1em")};
			margin-bottom: 15px;
			position: relative;
			p {
				display: inline-block;
				width: 90%;
				padding-left: ${(props) => (props.noDisc ? 0 : "10px")};
			}
		}
		li:before {
			${(props) => (props.noDisc ? "display: none;" : null)}
			content: "•";
			position: absolute;
			vertical-align: top;
			font-size: 1.25em;
			color: ${(props) => (props.discColor ? props.discColor : colors.newBrand.lightGrey)};
			margin-left: -0.8em;
		}
	}
	img {
		max-width: 100%;
	}
`;

export default ({ html, children, replaceNode, log, wrapNode, ...props }) => {
	return html ? (
		<HTMLWrapper {...props}>{parse(html, replaceNode)}</HTMLWrapper>
	) : children ? (
		<HTMLWrapper {...props}>{children}</HTMLWrapper>
	) : (
		<p>You must specify either and html or child attribute to wrap HTML</p>
	);
};
